import React, {
  useReducer,
  useEffect,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';

import * as f from 'f';
import {
  jiraApi,
} from 'chronos-api';
import {
  keyValueReducer,
} from 'chronos-reducers';
import * as selectors from 'chronos-selectors';
import * as actions from 'chronos-actions';

import Tabs from '@atlaskit/tabs';
import {
  Checkbox,
} from '@atlaskit/checkbox';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import DynamicTable from '@atlaskit/dynamic-table';
import ModalDialog, {
  ModalFooter,
  ModalHeader,
} from '@atlaskit/modal-dialog';
import TextField from '@atlaskit/textfield';
import {
  FieldTextStateless,
} from '@atlaskit/field-text';
import Button, {
  ButtonGroup,
} from '@atlaskit/button';
import {
  RadioGroup,
} from '@atlaskit/radio';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import {
  SelectAsyncPaginate,
} from 'chronos-components';

import {
  Flex,
} from 'chronos-styled';
import * as S from './styled';

const initialState = {
  isLoading: true,
  saveInProcess: false,
  testS3BucketInProcess: false,
  migrateToS3InProcess: false,
  showMigrateBanner: false,

  testS3Error: null,
  migrateS3Error: null,
  migratedFilesSize: 0,

  selectedTab: 0,
  usersSettings: [],
  deletedSettings: {},
  isAddUserModeEnabled: true,

  isValid: true,
  isSaved: false,
  testS3Url: null,
  settings: {
    screenshotsStorageSource: 'local',
    s3BucketName: '',
    s3BucketRegion: '',
    s3AccessKeyId: '',
    s3SecretKeyId: '',
  },
  errors: {
    s3BucketName: null,
    s3BucketRegion: null,
    s3AccessKeyId: null,
    s3SecretKeyId: null,
  },
};

const options = [
  { name: 'screenshotsStorageSource', value: 'local', label: 'Local server disk storage' },
  {name: 'screenshotsStorageSource', value: 's3', label: 'Amazon S3 bucket'},
];

const mapState = state => ({
  users: selectors.getResourceMap('users')(state),
});

export default ({
  isVisible,
  setVisibility,
}) => {
  const [state, dispatch] = useReducer(
    keyValueReducer,
    initialState,
  );
  const dispatchRedux = useDispatch();
  const { users } = useSelector(mapState);

  const validate = (settings = state.settings, saveResult = true) => {
    const {
      errors,
      valid,
    } = Object.keys(settings).reduce(
      (acc, key) => {
        if (acc.valid) {
          if (!settings[key]?.length) {
            acc.valid = false;
          }
        }
        acc.errors[key] = (
          settings[key]?.length
            ? null
            : 'is required'
        );
        return acc;
      },
      {
        errors: {},
        valid: true,
      },
    );
    const isValid = (
      state.settings.screenshotsStorageSource === 's3'
        ? valid
        : true
    );
    if (saveResult) {
      dispatch(actions.setState({
        errors,
        isValid,
      }));
    }
    return isValid;
  };

  const fetchSettings = async () => {
    const [
      settings,
      usersSettings,
    ] = await Promise.all([
      jiraApi.getServerSettings(),
      jiraApi.getAllUsersServerSettings(),
    ]);
    const outUsers = usersSettings.settings.filter(s => !users[s.accountId])
    if (outUsers.length) {
      dispatchRedux(actions.fetchFilterResourcesByIdRequest({
        users: outUsers.map(u => u.accountId)
      }));
    }
    await dispatch(actions.setState({
      settings,
      usersSettings: usersSettings.settings,
      isSaved: validate(settings, false),
    }));
  };

  const saveUsersSettings = async () => {
    dispatch(actions.setState({
      saveInProcess: true,
    }));
    await jiraApi.saveUsersSettings(({
      body: {
        settings: state.usersSettings.filter(s => s.isNew || s.isEdited),
        deleteIds: Object.keys(state.deletedSettings).map(accountId => accountId),
      },
    }));
    dispatch(actions.setState({
      saveInProcess: false,
    }));
  };

  const saveSettings = async () => {
    dispatch(actions.setState({
      saveInProcess: true,
    }));
    await jiraApi.saveServerSettings(({
      body: state.settings,
    }));
    dispatch(actions.setState({
      saveInProcess: false,
      isSaved: validate(state.settings, false),
    }));
  };

  const testS3Bucket = async () => {
    dispatch(actions.setState({
      testS3BucketInProcess: true,
    }));
    try {
      const {
        url,
        errorCode,
      } = await jiraApi.testS3Bucket();
      dispatch(actions.setState({
        testS3Url: url,
        testS3Error: errorCode,
        testS3BucketInProcess: false,
      }));
    } catch (err) {
      console.log(err);
      dispatch(actions.setState({
        testS3Error: '500',
        testS3BucketInProcess: false,
      }));
    }
  };

  const migrateToS3Bucket = async () => {
    dispatch(actions.setState({
      migrateToS3InProcess: true,
    }));
    try {
      const {
        size,
        errorCode,
      } = await jiraApi.migrateToS3();
      dispatch(actions.setState({
        showMigrateBanner: true,
        migrateS3Error: errorCode,
        migratedFilesSize: size,
        migrateToS3InProcess: false,
      }));
    } catch (err) {
      console.log(err);
      dispatch(actions.setState({
        showMigrateBanner: true,
        migrateS3Error: '500',
        migrateToS3InProcess: false,
      }));
    }
  };

  useEffect(
    () => {
      (async () => {
        dispatch(actions.setState({
          isLoading: true,
        }));
        if (isVisible) {
          await fetchSettings();
          await dispatch(actions.setState({
            isLoading: false,
          }));
        } else {
          dispatch(actions.setState({
            ...initialState,
          }));
        }
      })();
    },
    [isVisible],
  );

  if (!isVisible) {
    return null;
  }

  const head = {
    cells: [
      {
        key: 'name',
        content: 'User name',
        isSortable: true,
        width: 20,
      },
      {
        key: 'isScreenshotsRequired',
        content: 'Is screenshots required',
        shouldTruncate: true,
        isSortable: true,
        width: 20,
      },
      {
        key: 'screenshotPeriod',
        content: 'Screnshots period(min)',
        isSortable: false,
        width: 20,
      },
      {
        key: 'isHideMode',
        content: 'Do not show user his screenshots',
        shouldTruncate: true,
        isSortable: true,
        width: 20,
      },
      {
        key: 'isConstantScreen',
        content: 'Constant screen',
        shouldTruncate: true,
        isSortable: true,
        width: 20,
      },
      {
        key: 'screenshotConstantPeriod',
        content: 'Constant period(sec)',
        isSortable: false,
        width: 20,
      },
      {
        key: 'delete',
        shouldTruncate: true,
        width: 12,
      },
    ],
  };

  const getCurrentSetting = (settingId) =>
    (settings) => ([settings.findIndex(s => s.id === settingId), settings]);
  const markAsEdited = ([index, settings]) => {
    settings[index] = {
      ...settings[index],
      isEdited: true,
    };
    return settings;
  };
  const rows = state.usersSettings.map(
    (setting) => ({
      key: setting.id,
      cells: [
        {
          key: 'username',
          content: (
            <div>{users[setting.accountId]?.displayName || 'Unknown name'}</div>
          ),
        },
        {
          key: 'isScreenshotsRequired',
          content: (
            <Checkbox
              name="isScreenshotsRequired"
              isChecked={setting.isScreenshotsRequired}
              onChange={() => {
                const changeIsScreenshotsRequired = ([index, settings]) => {
                  settings[index] = f.toggle(settings[index], 'isScreenshotsRequired')
                  return [index, settings];
                };
                dispatch(actions.setState({
                  usersSettings: (
                    f.pipe(
                      getCurrentSetting(setting.id),
                      changeIsScreenshotsRequired,
                      markAsEdited,
                    )(state.usersSettings)
                  ),
                }));
              }}
            />
          ),
        },
        {
          key: 'screenshotsPeriod',
          content: (
            <TextField
              isCompact
              width="xsmall"
              type="Number"
              max={10}
              min={1}
              value={setting.screenshotsPeriod / 60}
              isDisabled={!setting.isScreenshotsRequired}
              onChange={(ev) => {
                let value = parseInt(ev.target.value, 10);
                if (value < 1) {
                  value = 1;
                }
                if (value > 10) {
                  value = 10;
                }
                const changeScreenshotsPeriod = ([index, settings]) => {
                  settings[index] = ({ ...settings[index], screenshotsPeriod: value * 60 });
                  return [index, settings];
                };
                dispatch(actions.setUiState({
                  usersSettings: (
                    f.pipe(
                      getCurrentSetting(setting.id),
                      changeScreenshotsPeriod,
                      markAsEdited,
                    )(state.usersSettings)
                  ),
                }));
              }}
            />
          ),
        },
        {
          key: 'isHideMode',
          content: (
            <Checkbox
              name="isHideMode"
              isChecked={setting.isHideMode}
              onChange={() => {
                const changeIsHideMode = ([index, settings]) => {
                  settings[index] = f.toggle(settings[index], 'isHideMode')
                  return [index, settings];
                };

                dispatch(actions.setState({
                  usersSettings: (
                    f.pipe(
                      getCurrentSetting(setting.id),
                      changeIsHideMode,
                      markAsEdited,
                    )(state.usersSettings)
                  ),
                }));
              }}
            />
          ),
        },
        {
          key: 'isConstantScreen',
          content: (
            <Checkbox
              name="isConstantScreen"
              isChecked={setting.isConstantScreen}
              onChange={() => {
                const changeIsHideMode = ([index, settings]) => {
                  settings[index] = f.toggle(settings[index], 'isConstantScreen')
                  return [index, settings];
                };

                dispatch(actions.setState({
                  usersSettings: (
                    f.pipe(
                      getCurrentSetting(setting.id),
                      changeIsHideMode,
                      markAsEdited,
                    )(state.usersSettings)
                  ),
                }));
              }}
            />
          ),
        },
        {
          key: 'screenshotsConstantPeriod',
          content: (
            <TextField
              isCompact
              width="xsmall"
              type="Number"
              max={100000000000}
              min={5}
              value={setting.screenshotsConstantPeriod || 600}
              isDisabled={!setting.isConstantScreen}
              onChange={(ev) => {
                let value = parseInt(ev.target.value, 10);
                const changeScreenshotsConstantPeriod = ([index, settings]) => {
                  settings[index] = ({ ...settings[index], screenshotsConstantPeriod: value });
                  return [index, settings];
                };
                dispatch(actions.setUiState({
                  usersSettings: (
                    f.pipe(
                      getCurrentSetting(setting.id),
                      changeScreenshotsConstantPeriod,
                      markAsEdited,
                    )(state.usersSettings)
                  ),
                }));
              }}
            />
          ),
        },
        {
          key: 'delete',
          content: (
            <div
              style={{
                cursor: 'pointer',
              }}
              onClick={() => {
                dispatch(actions.setState({
                  usersSettings: state.usersSettings.filter(s => s.id !== setting.id),
                  ...(
                    setting.isNew ? {} : {
                      deletedSettings: {
                        ...state.deletedSettings,
                        [setting.accountId]: setting.id,
                      }
                    }
                  )
                }));
              }}
            >
              <TrashIcon />
            </div>
          )
        },
      ],
    })
  );
  if (state.isAddUserModeEnabled) {
    rows.push({
      key: 'add-new-user',
      cells: [
        {
          key: 'new-user',
          content: (
            <SelectAsyncPaginate
              name="selectUser"
              placeholder="Select user"
              classNamePrefix="chronosPopupSelect"
              hideSelectedOptions
              closeMenuOnSelect
              controlShouldRenderValue
              openMenuOnFocus
              isMulti={false}
              isClearable={false}
              cacheUniq="users"
              cacheOptions="users"
              styles={{menuPortal: base => ({...base, zIndex: 9999})}}
              menuPortalTarget={document.body}
              filterOption={({value}) => !state.usersSettings.some(s => s.accountId === value)}
              loadOptions={(query, options) => (
                new Promise(
                  resolve => (
                    dispatchRedux(actions.loadFilterOptionsRequest({
                      filterType: 'users',
                      query,
                      loadedQty: options.length,
                      resolve,
                    }))
                  ),
                )
              )}
              onChange={(newUser) => {
                const {[newUser.value]: movedBackSetting, ...deletedSettings} = state.deletedSettings;
                dispatch(actions.setState({
                  usersSettings: [
                    ...state.usersSettings,
                    {
                      id: state.deletedSettings[newUser.value] || newUser.value,
                      accountId: newUser.value,
                      name: newUser.label,
                      isScreenshotsRequired: false,
                      screenshotsPeriod: 10 * 60,
                      isHideMode: false,
                      isConstantScreen: false,
                      isNew: true,
                    },
                  ],
                  deletedSettings,
                  isAddUserModeEnabled: false,
                }));
              }}
            />
          ),
        },
      ]
    });
  }

  const tabs = [
    {
      label: 'User settings',
      content: (
        <div>
          <DynamicTable
            head={head}
            rows={rows}
            isFixedSize
            defaultSortKey="name"
            defaultSortOrder="DESC"
          />
          <Button
            isDisabled={state.isAddUserModeEnabled}
            onClick={() => {
              dispatch(actions.setState({
                isAddUserModeEnabled: true,
              }));
            }}
          >
            Add
          </Button>
        </div>
      ),
    },
    {
      label: 'Screenshots settings',
      content: (
        <div style={{minHeight: 360}}>
          <Flex
            row
            align="flex-end"
            width="100%"
          >
            <div style={{ width: '80%' }}>
              <S.InputLabel>
                How to store screenshots
              </S.InputLabel>
              <RadioGroup
                options={options}
                value={state.settings.screenshotsStorageSource}
                defaultValue={options[0].value}
                onChange={(ev) => {
                  dispatch(actions.setState({
                    settings: {
                      ...state.settings,
                      screenshotsStorageSource: ev.target.value,
                    },
                  }));
                }}
              />
            </div>
          </Flex>
          {state.settings.screenshotsStorageSource === 's3' && (
            <>
              <Flex
                row
                align="flex-end"
                width="100%"
              >
                <Flex>
                  <FieldTextStateless
                    label="S3 Bucket region"
                    value={state.settings.s3BucketRegion}
                    onChange={(ev) => {
                      dispatch(actions.setState({
                        settings: {
                          ...state.settings,
                          s3BucketRegion: ev.target.value,
                        },
                      }));
                    }}
                    required
                    isInvalid={!!state.errors.s3BucketRegion}
                    invalidMessage={state.errors.s3BucketRegion}
                  />
                </Flex>
                <S.InputExample>
                  (eg. us-east-1)
                </S.InputExample>
              </Flex>
              <Flex
                row
                align="flex-end"
                width="100%"
              >
                <Flex>
                  <FieldTextStateless
                    label="S3 Bucket name"
                    value={state.settings.s3BucketName}
                    onChange={(ev) => {
                      dispatch(actions.setState({
                        settings: {
                          ...state.settings,
                          s3BucketName: ev.target.value,
                        },
                      }));
                    }}
                    required
                    isInvalid={!!state.errors.s3BucketName}
                    invalidMessage={state.errors.s3BucketName}
                  />
                </Flex>
              </Flex>
              <Flex
                row
                align="flex-end"
                width="80%"
              >
                <FieldTextStateless
                  label="Access Key Id"
                  shouldFitContainer
                  value={state.settings.s3AccessKeyId}
                  onChange={(ev) => {
                    dispatch(actions.setState({
                      settings: {
                        ...state.settings,
                        s3AccessKeyId: ev.target.value,
                      },
                    }));
                  }}
                  required
                  isInvalid={!!state.errors.s3AccessKeyId}
                  invalidMessage={state.errors.s3AccessKeyId}
                />
              </Flex>
              <Flex
                row
                align="flex-end"
                width="80%"
              >
                <FieldTextStateless
                  label="Secret Key Id"
                  shouldFitContainer
                  value={state.settings.s3SecretKeyId}
                  onChange={(ev) => {
                    dispatch(actions.setState({
                      settings: {
                        ...state.settings,
                        s3SecretKeyId: ev.target.value,
                      },
                    }));
                  }}
                  required
                  isInvalid={!!state.errors.s3SecretKeyId}
                  invalidMessage={state.errors.s3SecretKeyId}
                />
              </Flex>
              {(
                state.showMigrateBanner
                || state.migrateToS3InProcess
              )
                && (
                  <div style={{ marginTop: '10px' }}>
                    <SectionMessage
                      title={(
                        state.migrateS3Error
                          ? 'S3 bucket access error'
                          : 'Migration info'
                      )}
                    >
                      {state.migrateToS3InProcess
                        ? (
                          <div>
                            Migration in process...
                            <Spinner />
                          </div>
                        ) : (
                          <div>
                            {(
                              state.migrateS3Error
                                ? `Error: ${state.migrateS3Error}`
                                : (
                                  <div>
                                    <p>
                                      The migration to the Amazon S3 bucket was successfully finished!
                                    </p>
                                    <p>
                                      A number of migrated files:
                                      {state.migratedFilesSize}
                                    </p>
                                  </div>
                                )
                            )}
                          </div>
                        )
                      }
                    </SectionMessage>
                  </div>
                )
              }
              {(
                state.testS3Url
                || state.testS3Error
              )
                && (
                  <div style={{ marginTop: '10px' }}>
                    <SectionMessage
                      title={(
                        state.testS3Error
                          ? 'S3 bucket access error'
                          : 'Check the accessibility of Amazon S3 bucket'
                      )}
                      actions={(
                        state.testS3Error
                          ? []
                          : [{
                            key: 'testS3Url',
                            href: state.testS3Url,
                            text: 'Test Url',
                          }]
                      )}
                    >
                      <p>
                        {(
                          state.testS3Error
                            ? `Error: ${state.testS3Error}`
                            : 'Click Test url to make sure you have provided properly credentials.'
                        )}
                      </p>
                    </SectionMessage>
                  </div>
                )
              }
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <ModalDialog
      width="large"
      onClose={() => setVisibility(false)}
      footer={() => (
        <ModalFooter>
          <Flex
            row
            style={{
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <ButtonGroup>
              {(
                state.selectedTab === 1
                && state.settings.screenshotsStorageSource === 's3'
                && state.isSaved
                && validate(state.settings, false)
                && !state.migrateToS3InProcess
                && !state.saveInProcess
              ) && (
                <Button
                  appearance="link"
                  disabled={state.testS3BucketInProcess}
                  onClick={() => {
                    const isValid = validate();
                    if (isValid) {
                      testS3Bucket();
                    }
                  }}
                  iconAfter={(
                    state.testS3BucketInProcess
                      ? (
                        <Spinner />
                      ) : (
                        null
                      )
                  )}
                >
                  Test S3 bucket connection
                </Button>
              )}
              {(
                state.selectedTab === 1
                && state.settings.screenshotsStorageSource === 's3'
                && state.isSaved
                && validate(state.settings, false)
                && !state.testS3BucketInProcess
                && !state.saveInProcess
              ) && (
                <Button
                  appearance="link"
                  disabled={state.testS3BucketInProcess}
                  onClick={() => {
                    const isValid = validate();
                    if (isValid) {
                      migrateToS3Bucket();
                    }
                  }}
                  iconAfter={(
                    state.migrateToS3InProcess
                      ? (
                        <Spinner />
                      ) : (
                        null
                      )
                  )}
                >
                  Move all local screenshots to S3
                </Button>
              )}
              <Button
                appearance="primary"
                disabled={state.saveInProcess}
                onClick={() => {
                  if (state.selectedTab === 0) {
                    saveUsersSettings();
                  } else {
                    const isValid = validate();
                    console.log({ isValid });
                    if (isValid) {
                      saveSettings();
                    }
                  }
                }}
                iconAfter={(
                  state.saveInProcess
                    ? (
                      <Spinner invertColor />
                    ) : (
                      null
                    )
                )}
              >
                Save settings
              </Button>
              <Button
                appearance="subtle"
                onClick={() => {
                  setVisibility(false);
                }}
              >
                Close
              </Button>
            </ButtonGroup>
          </Flex>
        </ModalFooter>
      )}
      header={() => (
        <ModalHeader />
      )}
    >
      <Tabs
        selected={state.selectedTab}
        onSelect={(_, selectedTab) => {
          dispatch(actions.setState({
            selectedTab,
          }))
        }}
        tabs={tabs}
      />
    </ModalDialog>
  );
};
